/**
 * Geofence policy (pure): WARN if the worker is far from the geocoded session
 * address — never block check-in. This module only computes the warning; the
 * UI/business layer decides what to show. Geocoding of the address string is
 * done elsewhere (conceptually like `services/geocoding.ts`); here we only need
 * the resolved target coordinates.
 */

import { haversineKm } from './km'
import type { GeoPoint } from './types'

/** Default warn radius around the session address (metres). */
export const DEFAULT_GEOFENCE_RADIUS_METERS = 500

export interface GeofenceResult {
  /** True when the check-in point is inside the target radius. */
  withinRadius: boolean
  /** Straight-line distance from check-in to target, in metres. */
  distanceMeters: number
  /** True when the worker is outside the radius (show a non-blocking warning). */
  warn: boolean
}

/**
 * Compare a check-in location against the target session location.
 * @param checkIn The worker's captured check-in coordinates.
 * @param target The geocoded session address coordinates.
 * @param radiusMeters Warn radius in metres (default {@link DEFAULT_GEOFENCE_RADIUS_METERS}).
 */
export function checkGeofence(
  checkIn: GeoPoint,
  target: GeoPoint,
  radiusMeters: number = DEFAULT_GEOFENCE_RADIUS_METERS,
): GeofenceResult {
  const distanceMeters = haversineKm(checkIn, target) * 1000
  const withinRadius = distanceMeters <= radiusMeters
  return {
    withinRadius,
    distanceMeters,
    // Policy: warn-if-far, never block. `warn` is purely advisory.
    warn: !withinRadius,
  }
}

/**
 * A MANUAL arrival recorded further than this from the session address gets
 * a confirm step on the device ("You're about X km from the session address —
 * record arrival anyway?") and, once recorded, a persisted
 * `arrivalLocationFlag` on the session (owner decision after the 2026-10-08
 * field test: a check-in 6.3 km from the address went through with only the
 * advisory 500 m warning). GPS auto-arrival is unaffected — it is already
 * gated by the tight arrival geofence.
 */
export const REMOTE_ARRIVAL_FLAG_METERS = 1000

export interface RemoteArrivalAssessment {
  /** True when the fix is further than {@link REMOTE_ARRIVAL_FLAG_METERS} away. */
  remote: boolean
  /** Straight-line distance, km (rounded to 0.1 for display/persistence). */
  distanceKm: number
}

/**
 * Pure: is this arrival fix far enough from the address to confirm + flag?
 * `null` when there is nothing to compare against (no geocoded address).
 */
export function assessRemoteArrival(
  fix: GeoPoint | null | undefined,
  target: GeoPoint | null | undefined,
): RemoteArrivalAssessment | null {
  if (!fix || !target) return null
  const distanceMeters = haversineKm(fix, target) * 1000
  return {
    remote: distanceMeters > REMOTE_ARRIVAL_FLAG_METERS,
    distanceKm: Math.round(distanceMeters / 100) / 10,
  }
}
